import type {
  HomeJourneyActionKey,
  HomeJourneyPhase,
  HomeJourneyProfile,
  Mission,
  MoneyRecord,
  QuizAttempt,
} from './models';

export interface HomeJourneySignals {
  childId: string;
  profile: HomeJourneyProfile;
  records: MoneyRecord[];
  mission?: Mission;
  quizAttempts: QuizAttempt[];
  now?: Date;
}

export interface HomeJourneyAction {
  key: HomeJourneyActionKey;
  to: string;
  label: string;
  shortLabel: string;
}

export interface HomePersonalization {
  dateKey: string;
  phase: HomeJourneyPhase;
  phaseLabel: string;
  emotionKey: 'discover' | 'choose' | 'achieve' | 'connect';
  emotionStep: 0 | 1 | 2 | 3;
  title: string;
  copy: string;
  reason: string;
  pose: 'surprise' | 'wave' | 'think' | 'thumbs' | 'celebrate';
  dailyDoneCount: number;
  hasRecord: boolean;
  missionDone: boolean;
  economyDone: boolean;
  routeStatus: string;
  nextAction: HomeJourneyAction;
  showCelebration: boolean;
}

export function localDateKey(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function happenedToday(value: string | undefined, today: string): boolean {
  return Boolean(value && localDateKey(value) === today);
}

function nextMissingAction(hasRecord: boolean, missionDone: boolean, economyDone: boolean): HomeJourneyAction {
  if (!hasRecord) return { key: 'record', to: '/child/records', label: '오늘의 돈 기록 남기기', shortLabel: '돈 기록' };
  if (!missionDone) return { key: 'mission', to: '/child/missions', label: '핵심 미션에 도전하기', shortLabel: '미션 도전' };
  if (!economyDone) return { key: 'economy', to: '/child/concepts/economy', label: '설옹의 경제 퀴즈 풀기', shortLabel: '경제 퀴즈' };
  return { key: 'growth', to: '/child/growth-board', label: '오늘 완성한 성장판 보기', shortLabel: '성장판 보기' };
}

export function deriveHomePersonalization({ profile, records, mission, quizAttempts, now = new Date() }: HomeJourneySignals): HomePersonalization {
  const dateKey = localDateKey(now);
  const hasRecord = records.some((record) => record.occurredAt && localDateKey(record.occurredAt) === dateKey);
  const missionDone = Boolean(
    mission &&
    ['submitted', 'approved'].includes(mission.status) &&
    (happenedToday(mission.submittedAt, dateKey) || happenedToday(mission.approvedAt, dateKey))
  );
  const economyDone = quizAttempts.some((attempt) => happenedToday(attempt.completedAt, dateKey));
  const dailyDoneCount = [hasRecord, missionDone, economyDone].filter(Boolean).length;
  const baseAction = nextMissingAction(hasRecord, missionDone, economyDone);

  let phase: HomeJourneyPhase;
  if (!profile.welcomeCompletedAt) phase = 'welcome';
  else if (dailyDoneCount === 3) phase = 'celebrate';
  else if (dailyDoneCount === 0) phase = 'restart';
  else phase = 'momentum';

  if (phase === 'welcome') {
    return {
      dateKey, phase, phaseLabel: '첫 만남', emotionKey: 'discover', emotionStep: 0,
      title: '나의 첫 선택을 만나볼 시간',
      copy: '정답을 맞히는 것보다 내 돈을 바라보는 첫 걸음이 더 중요해요.',
      reason: '처음 만난 오늘은 가장 쉬운 행동부터 안내해요.',
      pose: 'wave', dailyDoneCount, hasRecord, missionDone, economyDone,
      routeStatus: dailyDoneCount ? `${dailyDoneCount}/3 준비됨` : '첫 걸음 준비',
      nextAction: { ...baseAction, label: '나의 첫 선택 시작하기' },
      showCelebration: false,
    };
  }

  if (phase === 'restart') {
    return {
      dateKey, phase, phaseLabel: '다시 시작', emotionKey: 'discover', emotionStep: 0,
      title: '오늘도 눈 한 줌부터 시작해요',
      copy: '어제의 속도와 비교하지 않아도 괜찮아요. 오늘의 작은 선택이면 충분해요.',
      reason: '오늘 활동이 아직 없어 가장 부담이 적은 첫 행동을 추천했어요.',
      pose: 'surprise', dailyDoneCount, hasRecord, missionDone, economyDone,
      routeStatus: '3단계 남음',
      nextAction: { ...baseAction, label: '오늘의 스노우볼 다시 굴리기' },
      showCelebration: false,
    };
  }

  if (phase === 'momentum') {
    const twoSteps = dailyDoneCount >= 2;
    return {
      dateKey, phase, phaseLabel: twoSteps ? '완주 직전' : '성장 중',
      emotionKey: twoSteps ? 'achieve' : 'choose',
      emotionStep: twoSteps ? 2 : 1,
      title: twoSteps ? '두 걸음이나 해냈어요' : '내가 돈의 방향을 정했어요',
      copy: twoSteps
        ? '마지막 한 걸음만 더하면 오늘의 스노우볼이 완성돼요.'
        : '한 번의 기록으로 내 돈의 흐름을 스스로 바꿀 수 있다는 힘이 보여요.',
      reason: `${dailyDoneCount}개 행동을 마쳐 다음 순서인 ${baseAction.shortLabel}을 추천했어요.`,
      pose: twoSteps ? 'thumbs' : 'think', dailyDoneCount, hasRecord, missionDone, economyDone,
      routeStatus: `${3 - dailyDoneCount}단계 남음`, nextAction: baseAction, showCelebration: false,
    };
  }

  return {
    dateKey, phase, phaseLabel: '오늘 완주', emotionKey: 'connect', emotionStep: 3,
    title: '오늘의 스노우볼 완성!',
    copy: '기록하고 선택하고 배운 힘이 하나의 성장 기억으로 뭉쳐졌어요.',
    reason: '오늘의 세 행동을 모두 마쳐 성장판과 가족 공유를 추천해요.',
    pose: 'celebrate', dailyDoneCount, hasRecord, missionDone, economyDone,
    routeStatus: '오늘 루트 완주',
    nextAction: { key: 'family', to: '/14', label: '가족과 오늘의 성장 나누기', shortLabel: '가족과 나누기' },
    showCelebration: profile.celebrationAcknowledgedDate !== dateKey,
  };
}

export function defaultHomeJourneyProfile(childId: string, now = new Date()): HomeJourneyProfile {
  const timestamp = now.toISOString();
  return {
    childId,
    lastPhase: 'welcome',
    lastActionKey: 'record',
    lastSeenDate: localDateKey(now),
    firstSeenAt: timestamp,
    updatedAt: timestamp,
  };
}
